import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import api, { formatApiError } from "@/lib/api";

export default function AccountSettings() {
  const { collector, refresh } = useCollectorAuth();

  return (
    <div className="space-y-6" data-testid="account-settings-page">
      <div>
        <div className="kicker kicker-red">Account Settings</div>
        <h1 className="h-display text-3xl md:text-4xl mt-1" data-testid="account-settings-heading">
          Manage your account.
        </h1>
        <p className="text-sm text-[var(--smoke)] mt-2 max-w-2xl">
          Update the contact info tied to your CollectorUp account. Your username, email, and
          public profile settings live in a future update — this page focuses on the essentials.
        </p>
      </div>

      <PersonalInfoSection collector={collector} onSaved={refresh} />
      <PasswordSection />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Personal Info
// ─────────────────────────────────────────────────────────────
function PersonalInfoSection({ collector, onSaved }) {
  const [firstName, setFirstName] = useState(collector?.first_name || "");
  const [lastName, setLastName] = useState(collector?.last_name || "");
  const [phone, setPhone] = useState(collector?.phone || "");
  const [instagram, setInstagram] = useState(
    collector?.instagram ||
    // Best-effort extract from social_urls.instagram if it's the only source.
    extractInstagramHandle(collector?.social_urls?.instagram) ||
    ""
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Rehydrate when the parent collector doc refreshes.
    setFirstName(collector?.first_name || "");
    setLastName(collector?.last_name || "");
    setPhone(collector?.phone || "");
    setInstagram(collector?.instagram || extractInstagramHandle(collector?.social_urls?.instagram) || "");
  }, [collector]);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) return toast.error("First name is required.");
    if (!lastName.trim()) return toast.error("Last name is required.");
    setBusy(true);
    try {
      await api.patch("/collector-auth/account", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        instagram_handle: instagram.trim().replace(/^@/, ""),
      });
      toast.success("Account info updated.");
      await onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save changes.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="account-settings-personal-form"
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[4px_4px_0_var(--ink)]"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck size={16} className="text-[var(--panini-red)]" />
        <div className="kicker kicker-black">Personal information</div>
      </div>
      <h2 className="font-display text-2xl mt-1">Contact info</h2>
      <p className="text-xs text-[var(--smoke)] mt-1">
        Used on your order receipts and packing slips.
      </p>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <Field label="First name *">
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            data-testid="account-first-name"
            maxLength={80}
            className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
          />
        </Field>
        <Field label="Last name *">
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            data-testid="account-last-name"
            maxLength={80}
            className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
          />
        </Field>
        <Field label="Phone number">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="account-phone"
            maxLength={40}
            placeholder="+1 555 123 4567"
            className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
          />
        </Field>
        <Field label="Instagram handle">
          <div className="flex items-stretch">
            <span className="inline-flex items-center border-2 border-r-0 border-[var(--ink)] rounded-l-lg px-3 bg-[var(--cream)] text-sm text-[var(--smoke)] font-mono">
              @
            </span>
            <input
              type="text"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value.replace(/^@/, ""))}
              data-testid="account-instagram"
              maxLength={30}
              placeholder="yourhandle"
              className="flex-1 border-2 border-[var(--ink)] rounded-r-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
            />
          </div>
        </Field>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-[var(--smoke)]">
          Email: <span className="font-bold text-[var(--ink)]" data-testid="account-email-readonly">{collector?.email}</span> · changing your email lives in a later update.
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="account-personal-save"
          className="btn-red text-sm"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────
// Password change (inline)
// ─────────────────────────────────────────────────────────────
function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current) return toast.error("Enter your current password.");
    if (next.length < 10) return toast.error("New password must be at least 10 characters.");
    if (next !== confirm) return toast.error("New passwords do not match.");
    if (next === current) return toast.error("New password must differ from your current password.");
    setBusy(true);
    try {
      await api.post("/collector-auth/password-change", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // The password-change endpoint may respond with a `reauth_required` shape.
      if (typeof detail === "object" && detail?.code === "reauth_required") {
        toast.error("Please sign in again to change your password.");
      } else {
        toast.error(formatApiError(detail) || "Could not update password.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="account-settings-password-form"
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[4px_4px_0_var(--ink)]"
    >
      <div className="flex items-center gap-2">
        <KeyRound size={16} className="text-[var(--panini-red)]" />
        <div className="kicker kicker-black">Password</div>
      </div>
      <h2 className="font-display text-2xl mt-1">Change password</h2>
      <p className="text-xs text-[var(--smoke)] mt-1">
        Choose a strong, memorable password — at least 10 characters. Your other sessions will be signed out.
      </p>

      <div className="mt-5 grid md:grid-cols-3 gap-4">
        <Field label="Current password">
          <PasswordInput
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            toggle={() => setShowCurrent((v) => !v)}
            testid="account-password-current"
          />
        </Field>
        <Field label="New password">
          <PasswordInput
            value={next}
            onChange={setNext}
            show={showNext}
            toggle={() => setShowNext((v) => !v)}
            testid="account-password-new"
          />
        </Field>
        <Field label="Confirm new password">
          <PasswordInput
            value={confirm}
            onChange={setConfirm}
            show={showNext}
            toggle={() => setShowNext((v) => !v)}
            testid="account-password-confirm"
          />
        </Field>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <button type="submit" disabled={busy} className="btn-red text-sm" data-testid="account-password-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Update password
        </button>
      </div>
    </form>
  );
}

function PasswordInput({ value, onChange, show, toggle, testid }) {
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        maxLength={200}
        className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-yellow)]"
      />
      <button
        type="button"
        onClick={toggle}
        className="absolute inset-y-0 right-0 px-2 text-[var(--smoke)] hover:text-[var(--ink)]"
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function extractInstagramHandle(url) {
  if (!url) return "";
  try {
    const m = String(url).match(/instagram\.com\/([a-zA-Z0-9._]+)/);
    return m ? m[1] : "";
  } catch { return ""; }
}
